const TRACKING_KEYS = [
  "gclid",
  "utm_campaign",
  "utm_adgroup",
  "utm_term",
  "utm_content",
  "utm_source",
  "utm_medium",
] as const;

type TrackingKey = (typeof TRACKING_KEYS)[number];

export type LeadTrackingParams = Record<TrackingKey, string>;

const STORAGE_KEY = "choice-wealth-lead-tracking";

function createEmptyTrackingParams(): LeadTrackingParams {
  return {
    gclid: "",
    utm_campaign: "",
    utm_adgroup: "",
    utm_term: "",
    utm_content: "",
    utm_source: "",
    utm_medium: "",
  };
}

function readStoredTrackingParams(): LeadTrackingParams {
  if (typeof window === "undefined") {
    return createEmptyTrackingParams();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return createEmptyTrackingParams();
    }

    const parsed = JSON.parse(raw) as Partial<LeadTrackingParams>;
    return TRACKING_KEYS.reduce((accumulator, key) => {
      accumulator[key] = typeof parsed[key] === "string" ? parsed[key] : "";
      return accumulator;
    }, createEmptyTrackingParams());
  } catch {
    return createEmptyTrackingParams();
  }
}

function writeStoredTrackingParams(params: LeadTrackingParams) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    // Ignore storage failures and continue with in-memory values.
  }
}

export function persistLeadTrackingFromLocation(url = typeof window !== "undefined" ? window.location.href : "") {
  if (!url) {
    return createEmptyTrackingParams();
  }

  const stored = readStoredTrackingParams();
  const parsedUrl = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://example.com");

  const merged = TRACKING_KEYS.reduce((accumulator, key) => {
    const value = parsedUrl.searchParams.get(key);
    accumulator[key] = value?.trim() || stored[key] || "";
    return accumulator;
  }, createEmptyTrackingParams());

  writeStoredTrackingParams(merged);
  return merged;
}

export function getLeadTrackingParams() {
  if (typeof window === "undefined") {
    return createEmptyTrackingParams();
  }

  return persistLeadTrackingFromLocation(window.location.href);
}

/* ------------------------------------------------------------------ */
/* GTM data layer events                                              */
/*                                                                    */
/* One payload builder feeds every lead-funnel event so the three     */
/* events (initiated → submitted → call scheduled) push an identical  */
/* structure into the GTM dataLayer.                                  */
/* ------------------------------------------------------------------ */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type LeadFormEventName =
  | "lead_form_initiated"
  | "lead_form_submitted"
  | "call_scheduled_successfully";

export type LeadFormFields = {
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Complete international phone number, e.g. "+919872416743". */
  phone?: string;
  consent?: boolean;
};

/* Google Ads Enhanced Conversion user data — first/last name, email and the
   full international phone number are nested here rather than kept flat on the
   event, so GTM can map them straight to the Enhanced Conversions fields. */
export type LeadUserData = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
};

export type LeadFormPayload = LeadTrackingParams & {
  event: LeadFormEventName;
  form_id: string;
  consent: boolean;
  page_url: string;
  user_data: LeadUserData;
};

export function buildLeadFormPayload(
  event: LeadFormEventName,
  formId: string,
  fields: LeadFormFields = {},
): LeadFormPayload {
  return {
    event,
    form_id: formId,
    consent: Boolean(fields.consent),
    page_url: typeof window !== "undefined" ? window.location.href : "",
    ...getLeadTrackingParams(),
    user_data: {
      first_name: fields.firstName?.trim() ?? "",
      last_name: fields.lastName?.trim() ?? "",
      email: fields.email?.trim() ?? "",
      phone_number: fields.phone?.trim() ?? "",
    },
  };
}

export function pushLeadDataLayerEvent(
  event: LeadFormEventName,
  formId: string,
  fields: LeadFormFields = {},
) {
  if (typeof window === "undefined") {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(buildLeadFormPayload(event, formId, fields));
}
